/**
 * AIDES-04 — Aides complémentaires · ÉTAPE 1 : contractualisation.
 *
 * Conditions (snapshot figé) → documents (sélection, génération, lecture) →
 * signature manuelle (dépôt des versions signées) → porte vers le recensement.
 *
 * ⛔ Aucune règle ici : l'état, ce qui manque et les transitions possibles sont
 * CALCULÉS PAR LE SERVEUR (`/api/aides/prestations/...`). L'écran n'affiche
 * « Recensement à démarrer » que si le serveur rend ce statut.
 * Lecture / téléchargement / impression : route GED commune
 * `/api/metier/documents/{id}/download`.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, FileText, Loader2, Printer, Upload } from "lucide-react";
import { API, getAuthHeaders, formatDate, formatDateTimeFR } from "./helpers";
import ChampDate from "../../../components/ui/champ-date";

export const ETATS = {
  A_CONTRACTUALISER: { label: "À contractualiser", famille: "info" },
  DOCUMENTS_GENERES: { label: "Documents générés", famille: "info" },
  EN_ATTENTE_SIGNATURE: { label: "En attente de signature", famille: "info" },
  SIGNE: { label: "Signé", famille: "success" },
  RECENSEMENT_A_DEMARRER: { label: "Recensement à démarrer", famille: "success" },
  RECENSEMENT_EN_COURS: { label: "Recensement en cours", famille: "success" },
  RECENSEMENT_TERMINE: { label: "Recensement terminé", famille: "success" },
};

const REMISES = [
  { value: "main_propre", label: "Remis en main propre" },
  { value: "courrier", label: "Envoyé par courrier" },
  { value: "email_manuel", label: "Envoyé par e-mail (manuel)" },
];

export const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 16, color: "var(--oh-text)", lineHeight: 1.6,
};
const bouton = {
  primaire: { background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)", border: "1px solid var(--oh-accent-solid)" },
  secondaire: { background: "transparent", color: "var(--oh-accent-text)", border: "1px solid var(--oh-accent)" },
  discret: { background: "transparent", color: "var(--oh-text-secondary)", border: "1px solid var(--oh-border)" },
};
export const champ = {
  height: 36, borderRadius: 8, border: "1px solid var(--oh-border)", padding: "0 8px",
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
};

export function Bouton({ niveau = "discret", children, style, ...props }) {
  return (
    <button
      type="button"
      {...props}
      style={{
        ...bouton[niveau], borderRadius: 8, padding: "6px 12px", fontWeight: 500,
        display: "inline-flex", alignItems: "center", gap: 8, cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.5 : 1, ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Encart({ famille = "info", children, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: `var(--oh-${famille}-bg)`, color: `var(--oh-${famille}-text)`,
        border: `1px solid var(--oh-${famille}-border)`, borderRadius: 8, padding: "8px 12px",
      }}
    >
      {children}
    </div>
  );
}

const CHAMPS = {
  beneficiaire_nom: "nom du bénéficiaire", adresse_complete: "adresse", adresse_rue: "adresse",
  cp_ville: "code postal / ville", telephone: "téléphone", email: "e-mail",
  dossier_reference: "référence dossier", fait_a: "lieu de signature",
  fait_le_jour: "date de signature", fait_le_mois: "date de signature", fait_le_annee: "date de signature",
};

// Particulier : montants sans « TTC » (AIDES-08, décision JJ 2026-10-06).
const euros = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0 })} €`;
const libelleTranches = (bareme) => (bareme || []).map((t, i, b) => ({
  tranche: i + 1 < b.length ? `de ${euros(t.a_partir_de)} à ${euros(b[i + 1].a_partir_de - 1)}` : `à partir de ${euros(t.a_partir_de)}`,
  forfait: euros(t.forfait),
}));

/** Voir / télécharger / imprimer un document par la route GED COMMUNE. */
export async function ouvrirDocumentGed(tenantId, documentId, mode, nom) {
  try {
    const res = await fetch(`${API}/api/metier/documents/${documentId}/download`, {
      headers: getAuthHeaders(tenantId), credentials: "include",
    });
    if (!res.ok) throw new Error(`Document illisible (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (mode === "telecharger") {
      const a = document.createElement("a");
      a.href = url; a.download = nom || `${documentId}.pdf`; a.click();
    } else {
      const fenetre = window.open(url, "_blank");
      if (mode === "imprimer" && fenetre) fenetre.addEventListener("load", () => fenetre.print());
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(e.message);
  }
}

/** `niveauOuvrir` : « Ouvrir la prestation » est l'action du moment (primaire) sauf tant qu'aucune aide n'est recensée (T-430c : un seul bouton plein par bloc). */
export function AidesContractualisationPanel({ dossierId, tenantId, onChange, niveauOuvrir = "primaire" }) {
  const [prestation, setPrestation] = useState(undefined);
  const [occupe, setOccupe] = useState("");
  const [remise, setRemise] = useState("main_propre");
  const [depots, setDepots] = useState({});
  const [voirHistorique, setVoirHistorique] = useState(false);
  const [faitA, setFaitA] = useState("");
  const [faitLe, setFaitLe] = useState("");

  const base = `${API}/api/aides/prestations/dossier/${dossierId}`;

  const appeler = useCallback(async (url, options = {}) => {
    const headers = getAuthHeaders(tenantId, options.json ? "application/json" : null);
    const res = await fetch(url, {
      ...options, headers, credentials: "include",
      body: options.json ? JSON.stringify(options.json) : options.body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = data.detail;
      throw new Error((d && (d.message || d)) || `Erreur ${res.status}`);
    }
    return data;
  }, [tenantId]);

  const charger = useCallback(async () => {
    try {
      const data = await appeler(base);
      setPrestation(data.prestation);
    } catch (e) {
      setPrestation(null);
      toast.error(e.message);
    }
  }, [appeler, base]);

  useEffect(() => { charger(); }, [charger]);

  const agir = async (cle, url, options) => {
    setOccupe(cle);
    try {
      const data = await appeler(url, { method: "POST", ...options });
      if (data.prestation) setPrestation(data.prestation);
      if (onChange) onChange();
      return data;
    } catch (e) {
      toast.error(e.message);
      return null;
    } finally {
      setOccupe("");
    }
  };

  const ouvrirDocument = (documentId, mode) => ouvrirDocumentGed(tenantId, documentId, mode);

  const deposer = async (code) => {
    const d = depots[code] || {};
    if (!d.fichier) { toast.error("Choisir le fichier signé"); return; }
    const form = new FormData();
    form.append("fichier", d.fichier);
    if (d.reference) form.append("reference", d.reference);
    if (d.date) form.append("date_signature", d.date);
    setOccupe(`signe-${code}`);
    try {
      const res = await fetch(`${base}/documents/${code}/signe`, {
        method: "POST", headers: getAuthHeaders(tenantId), credentials: "include", body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data.detail && (data.detail.message || data.detail)) || `Erreur ${res.status}`);
      setPrestation(data.prestation);
      setDepots((x) => ({ ...x, [code]: {} }));
      toast.success("Version signée déposée");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOccupe("");
    }
  };

  if (prestation === undefined) {
    return <div style={carte}><Loader2 className="animate-spin" size={16} aria-label="Chargement" /></div>;
  }

  if (prestation === null) {
    return (
      <div style={carte} data-testid="aides-contractualisation">
        <div style={{ fontWeight: 500, marginBottom: 8 }}>Aides complémentaires — contractualisation</div>
        <p style={{ color: "var(--oh-text-secondary)", marginBottom: 12 }}>
          Aucune prestation ouverte sur ce dossier. L'ouverture fige les conditions commerciales en vigueur.
        </p>
        <Bouton niveau={niveauOuvrir} data-variant={niveauOuvrir === "primaire" ? "primaire" : "discret"} data-testid="aides-ouvrir-prestation" disabled={!!occupe}
          onClick={() => agir("ouvrir", `${base}/ouvrir`)}>
          {occupe === "ouvrir" && <Loader2 className="animate-spin" size={14} />}
          Ouvrir la prestation
        </Bouton>
      </div>
    );
  }

  const p = prestation;
  const s = p.conditions_snapshot || {};
  const etat = ETATS[p.statut] || { label: p.statut, famille: "info" };
  const modifiable = ["A_CONTRACTUALISER", "DOCUMENTS_GENERES"].includes(p.statut);
  const selection = p.selection_documents || [];
  const docs = p.documents || {};
  const manquantes = p.pieces_signees_manquantes || [];
  const libelle = Object.fromEntries((p.registre_documents || []).map((r) => [r.code, r.libelle]));

  const basculerFacultatif = (code, coche) => {
    const suivante = coche ? [...selection, code] : selection.filter((c) => c !== code);
    agir("selection", `${base}/documents/selection`, { method: "PUT", json: { selection: suivante } });
  };

  return (
    <div style={{ ...carte, display: "flex", flexDirection: "column", gap: 16 }} data-testid="aides-contractualisation">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 500 }}>Aides complémentaires — contractualisation</div>
        <span data-testid="aides-prestation-statut" data-statut={p.statut}
          style={{ background: `var(--oh-${etat.famille}-bg)`, color: `var(--oh-${etat.famille}-text)`,
            border: `1px solid var(--oh-${etat.famille}-border)`, borderRadius: 8, padding: "2px 8px", fontWeight: 500 }}>
          {etat.label}
        </span>
      </div>

      {/* ── Conditions figées ── */}
      <section data-testid="aides-conditions">
        <div style={{ fontWeight: 500, marginBottom: 4 }}>
          Conditions contractuelles <span style={{ color: "var(--oh-text-muted)", fontWeight: 400 }}>
            (offre v{s.offre_version}, figée le {formatDate(s.fige_le)})</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 16, rowGap: 4 }}>
          <span style={{ color: "var(--oh-text-secondary)" }}>Forfait de prise en charge</span>
          <span data-testid="aides-conditions-forfait">{euros(s.forfait_ttc)}</span>
          <span style={{ color: "var(--oh-text-secondary)" }}>Forfait de résultat</span>
          <span data-testid="aides-conditions-bareme">
            {s.bareme_forfaits
              ? libelleTranches(s.bareme_forfaits).map((l) => <div key={l.tranche}>{l.tranche} → {l.forfait}</div>)
              : "Conditions antérieures : actualisées à la prochaine génération"}
          </span>
          <span style={{ color: "var(--oh-text-secondary)" }}>Assiette</span>
          <span>{s.assiette}</span>
          <span style={{ color: "var(--oh-text-secondary)" }}>Exclusions</span>
          <span>{(s.exclusions || []).join(", ")}{s.exclusions_derogation ? ` — ${s.exclusions_derogation}` : ""}</span>
          <span style={{ color: "var(--oh-text-secondary)" }}>Garantie d'obtention</span>
          <span>{s.garantie_obtention ? "Oui" : "Aucune"}</span>
        </div>
      </section>

      {/* ── Documents ── */}
      <section data-testid="aides-documents">
        <div style={{ fontWeight: 500, marginBottom: 8 }}>Documents</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(p.registre_documents || []).map((r) => {
            const d = docs[r.code] || {};
            const retenu = selection.includes(r.code);
            return (
              <div key={r.code} data-testid={`aides-doc-${r.code}`}
                style={{ border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <input type="checkbox" id={`sel-${r.code}`} checked={retenu}
                    disabled={r.obligatoire || !modifiable || !!occupe}
                    data-testid={`aides-sel-${r.code}`}
                    onChange={(e) => basculerFacultatif(r.code, e.target.checked)} />
                  <label htmlFor={`sel-${r.code}`} style={{ fontWeight: 500 }}>
                    <FileText size={14} style={{ display: "inline", marginRight: 4 }} />{r.libelle}
                  </label>
                  <span style={{ color: "var(--oh-text-muted)" }}>{r.obligatoire ? "obligatoire" : "facultatif"}</span>
                  {d.original_document_id && d.rendu_definitif === false && (
                    <span data-testid={`aides-doc-provisoire-${r.code}`}
                      style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", borderRadius: 8, padding: "0 8px" }}>
                      Modèle provisoire — original non intégré
                    </span>
                  )}
                </div>
                {d.original_document_id && d.rendu_definitif && (
                  <span data-testid={`aides-doc-definitif-${r.code}`} style={{ color: "var(--oh-success-text)" }}>
                    Original intégré
                  </span>
                )}
                {d.original_document_id && d.aides_presentees && (
                  <span data-testid={`aides-doc-aides-${r.code}`} style={{ color: "var(--oh-text-secondary)" }}>
                    {d.aides_presentees.etat === "ok"
                      ? `Aides potentielles présentées : ${(d.aides_presentees.lignes || []).length} (total estimé ${d.aides_presentees.total || "—"})`
                      : "Aucune aide fiable recensée sur ce dossier : le document n'en présente aucune et renvoie au recensement."}
                  </span>
                )}
                {d.original_document_id && (d.a_completer || []).length > 0 && (
                  <Encart famille="info" testid={`aides-doc-a-completer-${r.code}`}>
                    À compléter à la main (donnée absente du dossier) : {d.a_completer.map((c) => CHAMPS[c] || c).join(", ")}
                  </Encart>
                )}
                {d.original_document_id && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ color: "var(--oh-text-secondary)" }}>Généré le {formatDateTimeFR(d.genere_le)}</span>
                    <Bouton aria-label={`Voir ${r.libelle}`} onClick={() => ouvrirDocument(d.original_document_id, "voir")}><Eye size={14} />Voir</Bouton>
                    <Bouton aria-label={`Télécharger ${r.libelle}`} onClick={() => ouvrirDocument(d.original_document_id, "telecharger")}><Download size={14} />Télécharger</Bouton>
                    <Bouton aria-label={`Imprimer ${r.libelle}`} onClick={() => ouvrirDocument(d.original_document_id, "imprimer")}><Printer size={14} />Imprimer</Bouton>
                  </div>
                )}
                {d.signe_document_id && (
                  <div data-testid={`aides-signe-${r.code}`} style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <Encart famille="success">
                      Signé le {formatDate(d.signe_le)} · déposé par {d.signe_depose_par}
                      {d.signe_reference ? ` · réf. ${d.signe_reference}` : ""}
                    </Encart>
                    <Bouton aria-label={`Voir la version signée de ${r.libelle}`} onClick={() => ouvrirDocument(d.signe_document_id, "voir")}><Eye size={14} />Voir le signé</Bouton>
                  </div>
                )}
                {p.statut === "EN_ATTENTE_SIGNATURE" && retenu && d.original_document_id && !d.signe_document_id && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <input type="file" accept="application/pdf,image/jpeg,image/png"
                      aria-label={`Fichier signé : ${r.libelle}`} data-testid={`aides-fichier-${r.code}`}
                      onChange={(e) => setDepots((x) => ({ ...x, [r.code]: { ...x[r.code], fichier: e.target.files[0] } }))} />
                    <input style={champ} placeholder="Référence (ex. n° de recommandé)" aria-label="Référence de la remise"
                      value={(depots[r.code] || {}).reference || ""}
                      onChange={(e) => setDepots((x) => ({ ...x, [r.code]: { ...x[r.code], reference: e.target.value } }))} />
                    <ChampDate style={champ}  aria-label="Date de signature"
                      value={(depots[r.code] || {}).date || ""}
                      onChange={(e) => setDepots((x) => ({ ...x, [r.code]: { ...x[r.code], date: e.target.value } }))} />
                    <Bouton niveau="secondaire" data-testid={`aides-deposer-${r.code}`} disabled={!!occupe}
                      onClick={() => deposer(r.code)}>
                      {occupe === `signe-${r.code}` ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                      Déposer la version signée
                    </Bouton>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Ce qui manque + action suivante ── */}
      {["EN_ATTENTE_SIGNATURE", "SIGNE"].includes(p.statut) && manquantes.length > 0 && (
        <Encart famille="error" testid="aides-pieces-manquantes">
          Pièces signées manquantes : {manquantes.map((c) => libelle[c] || c).join(", ")}
        </Encart>
      )}
      {p.statut === "RECENSEMENT_A_DEMARRER" && (
        <Encart famille="success" testid="aides-porte-ouverte">
          Contractualisation terminée : le recensement des aides peut démarrer.
        </Encart>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {modifiable && (
          <>
            <input style={champ} value={faitA} onChange={(e) => setFaitA(e.target.value)}
              placeholder="Fait à (si connu)" aria-label="Lieu de signature" data-testid="aides-fait-a" />
            <ChampDate style={champ}  value={faitLe} onChange={(e) => setFaitLe(e.target.value)}
              aria-label="Date de signature prévue" data-testid="aides-fait-le" />
          </>
        )}
        {modifiable && (
          <Bouton niveau={p.statut === "A_CONTRACTUALISER" ? "primaire" : "discret"} data-testid="aides-generer"
            disabled={!!occupe} onClick={() => agir("generer", `${base}/documents/generer`,
              { json: { fait_a: faitA || null, fait_le: faitLe || null, regenerer: p.statut === "DOCUMENTS_GENERES" } })}>
            {occupe === "generer" && <Loader2 className="animate-spin" size={14} />}
            Générer les documents
          </Bouton>
        )}
        {p.statut === "DOCUMENTS_GENERES" && (
          <>
            <select style={champ} value={remise} onChange={(e) => setRemise(e.target.value)} aria-label="Mode de remise au client">
              {REMISES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <Bouton niveau="primaire" data-testid="aides-remettre" disabled={!!occupe}
              onClick={() => agir("remise", `${base}/transition`, { json: { vers: "EN_ATTENTE_SIGNATURE", remise_mode: remise } })}>
              Marquer comme remis au client
            </Bouton>
          </>
        )}
        {p.statut === "EN_ATTENTE_SIGNATURE" && (
          <Bouton niveau="primaire" data-testid="aides-constater-signature" disabled={!!occupe || manquantes.length > 0}
            onClick={() => agir("signe", `${base}/transition`, { json: { vers: "SIGNE" } })}>
            Constater la signature
          </Bouton>
        )}
        {p.statut === "SIGNE" && (
          <Bouton niveau="primaire" data-testid="aides-ouvrir-recensement" disabled={!!occupe || !p.porte_recensement_ouvrable}
            onClick={() => agir("porte", `${base}/transition`, { json: { vers: "RECENSEMENT_A_DEMARRER" } })}>
            Ouvrir le recensement
          </Bouton>
        )}
      </div>

      {/* ── Historique ── */}
      <section>
        <Bouton aria-expanded={voirHistorique} data-testid="aides-historique-toggle"
          onClick={() => setVoirHistorique((v) => !v)}>
          {voirHistorique ? "Masquer l'historique" : `Voir l'historique (${(p.historique || []).length})`}
        </Bouton>
        {voirHistorique && (
          <ul data-testid="aides-historique" style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {[...(p.historique || [])].reverse().map((h, i) => (
              <li key={i} style={{ color: "var(--oh-text-secondary)" }}>
                {formatDateTimeFR(h.le)} · {h.par} · {h.action}
                {h.de || h.vers ? ` (${h.de ? `${ETATS[h.de]?.label || h.de} → ` : ""}${ETATS[h.vers]?.label || h.vers || ""})` : ""}
                {h.details?.document ? ` · ${libelle[h.details.document] || h.details.document}` : ""}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
